import { RefObject, useLayoutEffect } from 'react';

import { clampAutoColumnWidth } from '../utils/sessionColumnState';

interface FreezeColumnWidthsOptions {
  /** Ref to the `<table>` whose header cells carry `data-col-key`. */
  tableRef: RefObject<HTMLTableElement | null>;
  /** True once every visible column already has an explicit width. */
  isFrozen: boolean;
  /** Only measure once real rows are present — an empty body would size every
   *  column to just its (short) header text. */
  ready: boolean;
  /** Commits the measured widths; a single call seeds all columns via `seed`. */
  onColumnResize?: (key: string, width: number, seed?: Record<string, number>) => void;
}

/**
 * Freeze a content-sized list table into the precise fixed-pixel model: right
 * after the first paint that has data, measure each column's rendered CSS width
 * (`offsetWidth`, which is unaffected by the app's CSS `zoom`) and commit them
 * as fixed widths. Auto-layout can't shrink a column below its content, so this
 * freeze is what makes dragging accurate and columns shrinkable. Shared by every
 * resizable list table so the behaviour stays identical across them.
 */
export const useFreezeColumnWidths = ({
  tableRef,
  isFrozen,
  ready,
  onColumnResize,
}: FreezeColumnWidthsOptions): void => {
  useLayoutEffect(() => {
    if (isFrozen || !ready || !onColumnResize) return;
    const table = tableRef.current;
    if (!table) return;
    const seed: Record<string, number> = {};
    let anyKey: string | undefined;
    table.querySelectorAll<HTMLElement>('thead th[data-col-key]').forEach((th) => {
      const key = th.dataset.colKey;
      if (!key) return;
      seed[key] = clampAutoColumnWidth(th.offsetWidth || 120);
      if (anyKey === undefined) anyKey = key;
    });
    if (anyKey !== undefined) onColumnResize(anyKey, seed[anyKey], seed);
  }, [tableRef, isFrozen, ready, onColumnResize]);
};
